import React, { useCallback, useEffect, useMemo, useState } from "react";
import * as Mui from "@mui/material";
import { useSelector } from "react-redux";
import moment from "moment";
import { Box } from "@mui/material";
import { ThemeProvider } from "@mui/material/styles";
import { useFetch } from "services/hooks/useFetch";
import { theme, defaultAllSelection } from "services/constants";
import { Loader } from "components/Loader";
import sessionHandling from "services/utils/notificationUtils";
import { showToast } from "services/utils/Status";
import { CustomFilter } from "components/CustomComponents/CustomFilter";
import { TableEmptyState } from "components/CustomComponents/EmptyState";
import { FilterProvider } from "pages/FilterContext";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import Groups2Rounded from "@mui/icons-material/Groups2Rounded";
import BeachAccessRounded from "@mui/icons-material/BeachAccessRounded";
import MoneyOffRounded from "@mui/icons-material/MoneyOffRounded";
import EventBusyRounded from "@mui/icons-material/EventBusyRounded";
import AssignmentIndRounded from "@mui/icons-material/AssignmentIndRounded";
import { checkViewerPermission, subModuleNameMatches } from "services/utils/HelperFunction";
import { leaveReportKeyAndHeader } from "pages/TeamZone/TeamZoneHeaders";
import { ApprovalDateRangeFilter } from "components/CustomComponents/ApprovalDateRangeFilter";
import { getThisMonthRange } from "components/CustomComponents/dateRangeDefaults";
import { CompactHeaderSelectFilter } from "components/CustomComponents/CompactHeaderSelectFilter";
import { useSavedViewSelector } from "components/CustomComponents/useSavedViewSelector";
import { useExcelDownload } from "components/CustomComponents/useExcelDownload";
import {
  APP_BAR_OFFSET,
  designColors,
  designShell,
  paginationFooter,
  rowsPerPageOptions,
  StatCard,
  withDesignFont,
} from "services/constants/designTokens";
import {
  approveStickyCustomFilterBar,
  approveStickyFooterBar,
  approveTableBodyCell,
  approveTableHeadCell,
  ApproveTable,
} from "pages/TeamZone/Approve/approveUi";

export const LeaveAndPermission = ({
  setShowReport,
  setReportHeaderActions,
  embeddedReportTypes,
  selectedReportType,
  onReportTypeChange,
}) => {
  const { domain, token, userDetails, topbarCompanyId, userScreenAccess, topbarCompanyName } =
    useSelector((state) => state.tokenReducer);
  const [isLoading, setIsLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [leaveReportDetails, setLeaveReportDetails] = useState([]);
  const [leaveReportData, setLeaveReportData] = useState([]);
  const [initialDateRange] = useState(() =>
    getThisMonthRange({ endAt: "today" }),
  );
  const [startDate, setStartDate] = useState(initialDateRange.startDate);
  const [endDate, setEndDate] = useState(initialDateRange.endDate);
  const [dateRangeLabel, setDateRangeLabel] = useState(initialDateRange.label);
  const { result: departmentData } = useFetch(
    `${domain}get-department/?user_id=${userDetails.id}&company_id=${topbarCompanyId}`,
  );
  const [departmentName, setDepartmentName] = useState("All");
  const [departmentId, setDepartmentId] = useState(defaultAllSelection);
  const [customFilterHeight, setCustomFilterHeight] = useState(0);
  const [customFilterNode, setCustomFilterNode] = useState(null);
  const [footerNode, setFooterNode] = useState(null);
  const [footerHeight, setFooterHeight] = useState(0);
  const customFilterRef = useCallback((node) => setCustomFilterNode(node), []);
  const footerRef = useCallback((node) => setFooterNode(node), []);
  const { downloadExcel, downloadPdf, downloadCsv } = useExcelDownload();

  const teamZoneReports = userScreenAccess?.module_details?.find(
    (mod) => mod.module_name === "Reports",
  );

  // Both of these used to key off "Leave and Permission Report" (singular)
  // — not a real payload value (which comes back as "Leave and
  // Permission", no "Report" at all) and not even the alias table's own
  // canonical key ("Leave and Permission Reports", plural), so this
  // permission check silently failed and the raw === below always landed
  // on undefined, sending an invalid submodule id when saving a view. The
  // registry's own reportName for this screen (reportRegistry.js) is
  // already the plural form, so both checks now match that.
  const subModuleId = teamZoneReports?.submodules?.find((sub) =>
    subModuleNameMatches(sub.sub_module_name, "Leave and Permission Reports"),
  )?.sub_module_id;

  const check_access = checkViewerPermission(
    userScreenAccess?.module_details,
    "Reports",
    "Leave and Permission Reports",
  );

  const handleSavedViewChange = useCallback(() => {
    setPage(0);
  }, []);

  const { visibleHeaders, titleOverride } = useSavedViewSelector({
    domain,
    userId: userDetails?.id,
    subModuleId,
    storageKey: "leave_permission_report_saved_views",
    fullHeaders: leaveReportKeyAndHeader,
    screenAccess: check_access,
    onViewChange: handleSavedViewChange,
  });

  useEffect(() => {
    const fetchUsertaskDetails = async () => {
      try {
        setIsLoading(true);
        const response = await fetch(
          `${domain}leave_and_absent_reports/?user_id=${userDetails.id}&start_date=${startDate}&end_date=${endDate}&department_id=${departmentId}&company_id=${topbarCompanyId}`,
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `token ${token}`,
            },
          },
        );
        const res = await response.json();
        if (response.ok) {
          const rows = res?.leave_requests || [];
          setLeaveReportDetails(rows);
          setLeaveReportData(rows);
        } else if (response.status === 409) {
          setLeaveReportDetails([]);
          setLeaveReportData([]);
          sessionHandling();
        } else {
          setLeaveReportDetails([]);
          setLeaveReportData([]);
          throw new Error('REQUEST_FAILED');
        }
      } catch (error) {
        console.error('[LeaveAndPermission] operation failed:', error);
        setLeaveReportDetails([]);
        setLeaveReportData([]);
        showToast("error", "Couldn't load the leave report. Please try again.");
      } finally {
        setIsLoading(false);
      }
    };
    fetchUsertaskDetails();
  }, [
    domain,
    token,
    startDate,
    endDate,
    departmentId,
    userDetails.id,
    topbarCompanyId,
  ]);

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleStartDate = (rangeStart, rangeEnd, rangeLabel) => {
    const parsedStartDate = moment(rangeStart);
    const parsedEndDate = moment(rangeEnd);

    if (!parsedStartDate.isValid() || !parsedEndDate.isValid()) {
      return;
    }

    setStartDate(parsedStartDate.format("YYYY-MM-DD"));
    setEndDate(parsedEndDate.format("YYYY-MM-DD"));
    setDateRangeLabel(rangeLabel || "Custom Range");
    setPage(0);
  };

  const handleFilteredDataChange = useCallback(
    (filteredData) => {
      setLeaveReportData(filteredData);
      if (page * rowsPerPage >= filteredData.length) setPage(0);
    },
    [page, rowsPerPage],
  );

  const departmentList = useMemo(
    () => [
      {
        id: defaultAllSelection,
        label: "All",
        department_name: "All",
      },
      ...(departmentData?.map((obj) => ({
        ...obj,
        label: obj.department_name,
      })) ?? []),
    ],
    [departmentData],
  );

  const updateDepartmentName = (event, data) => {
    if (data && data?.label) {
      setDepartmentName(data.label);
      setDepartmentId(data.id);
      setPage(0);
    }
  };

  const getReportMetadata = useCallback(() => {
    const formattedStartDate = moment(
      startDate,
      ["DD/MM/YYYY", "YYYY-MM-DD"],
      true,
    ).format("DD/MM/YYYY");
    const formattedEndDate = moment(
      endDate,
      ["DD/MM/YYYY", "YYYY-MM-DD"],
      true,
    ).format("DD/MM/YYYY");

    const rows = leaveReportData || [];
    const sumField = (key) =>
      rows.reduce((sum, row) => sum + Number(row?.[key] || 0), 0);

    return {
      "Company Name": topbarCompanyName || "N/A",
      Department: departmentName || "All",
      "Date Range": `${formattedStartDate} to ${formattedEndDate}`,
      "Report Generated On": moment().format("DD/MM/YYYY HH:mm:ss"),
      Employees: rows.length,
      "Paid Leave": sumField("paid_leave_count"),
      "Unpaid Leave": sumField("unpaid_leave_count"),
      Absent: sumField("absent_count"),
      Permission: sumField("permission_count"),
    };
  }, [departmentName, endDate, leaveReportData, startDate, topbarCompanyName]);

  const handleLeaveReportDownload = useCallback(
    async (exportRows, onProgress, downloadFormat = "excel") => {
      const rows = Array.isArray(exportRows) ? exportRows : [];
      // Type-driven off visibleHeaders (not a fixed key list) so every
      // number column sums automatically — leaveReportKeyAndHeader has 6
      // (paid/unpaid leave, absent, permission, missing, late/early), and a
      // fixed list previously summed only 4, silently dropping missing_count
      // and late_early_count from the TOTAL row whenever either was visible.
      const summaryRow = { rowType: "grand_total", username: "TOTAL" };
      visibleHeaders.forEach((header) => {
        if (header.type === "number") {
          summaryRow[header.key] = rows.reduce(
            (sum, row) => sum + Number(row?.[header.key] || 0),
            0,
          );
        }
      });

      const exportToFormat =
        downloadFormat === "pdf"
          ? downloadPdf
          : downloadFormat === "csv"
            ? downloadCsv
            : downloadExcel;

      await exportToFormat(
        [...rows, summaryRow],
        visibleHeaders,
        "Leave and Permission",
        {},
        getReportMetadata(),
        [],
      );

      if (typeof onProgress === "function") {
        onProgress(100);
      }
    },
    [downloadCsv, downloadExcel, downloadPdf, getReportMetadata, visibleHeaders],
  );

  // Same StatCard row Working Hours' Summary/Detailed/Time Card and Overtime
  // Analysis show above their table, derived from the currently visible
  // (searched/filtered) rows.
  const leaveReportSummaryCards = useMemo(() => {
    const rows = leaveReportData || [];
    const sumField = (key) =>
      rows.reduce((sum, row) => sum + Number(row?.[key] || 0), 0);

    return [
      {
        key: "employees",
        title: "Employees",
        value: rows.length,
        icon: Groups2Rounded,
        accent: "#1D4ED8",
        tint: "#EEF4FF",
      },
      {
        key: "paidLeave",
        title: "Paid Leave",
        value: sumField("paid_leave_count"),
        icon: BeachAccessRounded,
        accent: "#0F766E",
        tint: "#E6F8F4",
      },
      {
        key: "unpaidLeave",
        title: "Unpaid Leave",
        value: sumField("unpaid_leave_count"),
        icon: MoneyOffRounded,
        accent: "#F97316",
        tint: "#FFF7ED",
      },
      {
        key: "absent",
        title: "Absent",
        value: sumField("absent_count"),
        icon: EventBusyRounded,
        accent: "#EF4444",
        tint: "#FEF2F2",
      },
      {
        key: "permission",
        title: "Permission",
        value: sumField("permission_count"),
        icon: AssignmentIndRounded,
        accent: "#7C3AED",
        tint: "#F5F3FF",
      },
    ];
  }, [leaveReportData]);

  const selectedReportTypeOption =
    embeddedReportTypes?.find(
      (option) =>
        option?.label === selectedReportType ||
        option?.value === selectedReportType,
    ) || null;

  const headerFilterControls = useMemo(
    () => (
      <Mui.Stack
        direction="row"
        spacing={1.5}
        useFlexGap
        flexWrap="wrap"
        justifyContent="flex-start"
        sx={{
          width: "100%",
          alignItems: "center",
        }}
      >
        <ApprovalDateRangeFilter
          startDate={startDate}
          endDate={endDate}
          dateRangeLabel={dateRangeLabel}
          onChange={handleStartDate}
          showToday={false}
          includeCurrentDate={false}
          thisMonthToDate
          sx={{
            minHeight: 40,
            height: 40,
            px: 2,
            width: { xs: "100%", sm: "auto" },
            backgroundColor: designColors.slate100,
            border: "none",
            boxShadow: "none",
            "&:hover": {
              backgroundColor: designColors.slate100,
              border: "none",
              boxShadow: "none",
            },
          }}
        />
        <CompactHeaderSelectFilter
          dataList={departmentList}
          selectedValue={departmentName}
          updateDataFunction={updateDepartmentName}
          Icons={PersonOutlineIcon}
          staticLabel="Department"
          widthSx={{
            width: { xs: "100%", sm: "auto" },
            minWidth: { xs: "100%", sm: 220 },
          }}
        />
        {embeddedReportTypes?.length ? (
          <CompactHeaderSelectFilter
            dataList={embeddedReportTypes}
            selectedValue={selectedReportTypeOption}
            updateDataFunction={onReportTypeChange}
            Icons={PersonOutlineIcon}
            staticLabel="Report Type"
            widthSx={{
              width: { xs: "100%", sm: "auto" },
              minWidth: { xs: "100%", sm: 260 },
            }}
          />
        ) : null}
      </Mui.Stack>
    ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      startDate,
      endDate,
      dateRangeLabel,
      departmentList,
      departmentName,
      embeddedReportTypes,
      selectedReportTypeOption,
      onReportTypeChange,
    ],
  );

  // Filters render inline in the page body (matching Employee Attendance's
  // own row below the title), not floated beside the title.
  useEffect(() => {
    if (!setReportHeaderActions) return undefined;
    setReportHeaderActions(null);
    return () => setReportHeaderActions(null);
  }, [setReportHeaderActions]);

  useEffect(() => {
    if (!customFilterNode) return undefined;
    const updateHeight = () =>
      setCustomFilterHeight(customFilterNode.offsetHeight || 0);
    updateHeight();
    if (typeof ResizeObserver === "undefined") return undefined;
    const observer = new ResizeObserver(updateHeight);
    observer.observe(customFilterNode);
    return () => observer.disconnect();
  }, [customFilterNode, visibleHeaders.length]);

  useEffect(() => {
    if (!footerNode) return undefined;
    const updateHeight = () => setFooterHeight(footerNode.offsetHeight || 0);
    updateHeight();
    if (typeof ResizeObserver === "undefined") return undefined;
    const observer = new ResizeObserver(updateHeight);
    observer.observe(footerNode);
    return () => observer.disconnect();
  }, [footerNode]);

  return (
    <ThemeProvider theme={withDesignFont(theme)}>
      <FilterProvider
        domain={domain}
        userDetails={userDetails}
        subModuleId={subModuleId}
      >
        <Box sx={{ pb: 0, width: "100%", maxWidth: "100%" }}>
          {/* Filters sit in their own row directly under the page title —
              same placement as Employee Attendance Report — instead of
              floating beside the title via setReportHeaderActions. */}
          <Mui.Box sx={{ ...designShell.sectionCard, p: 1.5, mb: 2 }}>
            {headerFilterControls}
          </Mui.Box>
          <Mui.Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                sm: "repeat(2, minmax(0, 1fr))",
                md: "repeat(2, minmax(0, 1fr))",
                lg: "repeat(3, minmax(0, 1fr))",
                xl: "repeat(5, minmax(0, 1fr))",
              },
              gap: 1.5,
              mb: 2,
            }}
          >
            {leaveReportSummaryCards.map((card) => (
              <StatCard
                key={card.key}
                icon={card.icon}
                title={card.title}
                value={card.value}
                accent={card.accent}
                tint={card.tint}
              />
            ))}
          </Mui.Box>

          <Box ref={customFilterRef} sx={approveStickyCustomFilterBar}>
            <CustomFilter
              title="Leave and Permission"
              titleOverride={titleOverride}
              filterHeaders={visibleHeaders}
              data={leaveReportDetails}
              filteredData={leaveReportData}
              onFilteredDataChange={handleFilteredDataChange}
              screenAccess={check_access}
              showtitle_count={true}
              hideCount={true}
              isAttendanceScreenReport={true}
              showDownloadOptions={false}
              downloadFormats={["excel", "pdf", "csv"]}
              includeMetadata={true}
              metadata={getReportMetadata()}
              customDownloadHandler={handleLeaveReportDownload}
              labeledToolbar={true}
              searchPlaceholder="Search by employee, department..."
            />
          </Box>

          <Box sx={{ ...designShell.sectionCard, mt: 1.5, borderRadius: 0 }}>
            {isLoading ? (
              <Loader info="Loading..." />
            ) : (
              <Mui.Box>
                <Mui.TableContainer
                  sx={{
                    boxSizing: "border-box",
                    width: "100%",
                    maxWidth: "100%",
                    overflowX: { xs: "auto", sm: "visible" },
                    overflowY: { xs: "auto", sm: "visible" },
                    pb: footerHeight ? `${footerHeight}px` : 0,
                    borderLeft: `1px solid ${designColors.slate200}`,
                    borderRight: `1px solid ${designColors.slate200}`,
                    borderBottom: `1px solid ${designColors.slate200}`,
                  }}
                >
                  <ApproveTable stickyHeader sx={{ minWidth: 900, width: "100%" }}>
                    <Mui.TableHead sx={designShell.tableStickyHead}>
                      <Mui.TableRow sx={designShell.tableStickyHeadRow}>
                        {visibleHeaders.map((header, headerIndex) => (
                          <Mui.TableCell
                            key={header.key}
                            sx={{
                              ...approveTableHeadCell,
                              top: APP_BAR_OFFSET + customFilterHeight,
                              ...(headerIndex === 0
                                ? { borderTopLeftRadius: 0 }
                                : {}),
                              ...(headerIndex === visibleHeaders.length - 1
                                ? { borderTopRightRadius: 0 }
                                : {}),
                            }}
                          >
                            {header.name}
                          </Mui.TableCell>
                        ))}
                      </Mui.TableRow>
                    </Mui.TableHead>
                    <Mui.TableBody>
                      {leaveReportData?.length === 0 ? (
                        <TableEmptyState dataLength={leaveReportDetails} />
                      ) : (
                        leaveReportData
                          ?.slice(
                            page * rowsPerPage,
                            page * rowsPerPage + rowsPerPage,
                          )
                          .map((row, rowIndex) => (
                            <Mui.TableRow
                              key={rowIndex}
                              hover
                              sx={{
                                ...designShell.tableRowHover,
                                "&:last-child td": { borderBottom: "none" },
                                "& td": { backgroundColor: designColors.white },
                              }}
                            >
                              {visibleHeaders.map((header) => (
                                <Mui.TableCell
                                  key={header.key}
                                  sx={approveTableBodyCell}
                                >
                                  {row[header.key] ? row[header.key] : "0"}
                                </Mui.TableCell>
                              ))}
                            </Mui.TableRow>
                          ))
                      )}
                    </Mui.TableBody>
                  </ApproveTable>
                </Mui.TableContainer>
              </Mui.Box>
            )}

            <Mui.Box ref={footerRef} sx={approveStickyFooterBar}>
              {paginationFooter({
                page,
                rowsPerPage,
                total: leaveReportData?.length || 0,
                itemLabel: "records",
                onPageChange: setPage,
                onRowsPerPageChange: handleChangeRowsPerPage,
                rowsPerPageOptions,
              })}
            </Mui.Box>
          </Box>
        </Box>
      </FilterProvider>
    </ThemeProvider>
  );
};
